import React, { useEffect, useState } from "react";
import { api, downloadFile, fmtINR, fmtNum, todayISO, fmtDate } from "../lib/api";
import { exportCSV, printPDF, fmtINRPlain } from "../lib/export";
import { FileBarChart2, TrendingUp, TrendingDown, Download, Printer, Trash2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { key: "sales", label: "Sales" },
  { key: "daily", label: "Daily Sales" },
  { key: "purchases", label: "Purchases" },
  { key: "pl", label: "P&L" },
  { key: "product", label: "Product-wise" },
  { key: "stock", label: "Stock Forecast" },
];

const last30 = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

export default function Reports() {
  const [tab, setTab] = useState("sales");
  const [from, setFrom] = useState(last30());
  const [to, setTo] = useState(todayISO());
  const [salesChannel, setSalesChannel] = useState("all");
  const [sales, setSales] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [pl, setPl] = useState(null);
  const [productAgg, setProductAgg] = useState([]);
  const [stock, setStock] = useState(null);
  const [dailyDate, setDailyDate] = useState(todayISO());
  const [daily, setDaily] = useState(null);

  const load = async () => {
    if (tab === "sales") {
      const ch = salesChannel !== "all" ? `&channel=${salesChannel}` : "";
      const res = await api.get(`/sales?from_date=${from}&to_date=${to}&limit=1000${ch}`);
      setSales(res.data);
    } else if (tab === "purchases") {
      const res = await api.get(`/purchases?from_date=${from}&to_date=${to}`);
      setPurchases(res.data);
    } else if (tab === "pl") {
      const res = await api.get(`/reports/pl?from_date=${from}&to_date=${to}`);
      setPl(res.data);
    } else if (tab === "product") {
      const res = await api.get(`/reports/product-sales?from_date=${from}&to_date=${to}`);
      setProductAgg(res.data);
    } else if (tab === "stock") {
      const res = await api.get(`/reports/stock-forecast?lookback_days=7`);
      setStock(res.data);
    } else if (tab === "daily") {
      const res = await api.get(`/reports/daily-sales?date=${dailyDate}`);
      setDaily(res.data);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [tab, from, to, salesChannel, dailyDate]);

  const doExportCSV = () => {
    const tag = `${tab}_${from}_${to}`;
    if (tab === "sales") {
      exportCSV(
        `sales_${tag}.csv`,
        [
          { key: "date", label: "Date" },
          { key: "channel", label: "Channel" },
          { key: "customer_name", label: "Customer" },
          { key: "total_kg", label: "Kg", value: (r) => Number(r.total_kg).toFixed(3) },
          { key: "total_amount", label: "Amount (INR)", value: (r) => Number(r.total_amount).toFixed(2) },
          { key: "paid_amount", label: "Paid (INR)", value: (r) => Number(r.paid_amount || 0).toFixed(2) },
          { key: "items", label: "Items", value: (r) => r.items.map((i) => `${i.product_name} ${i.kg}${i.unit==="piece"?"pc":"kg"}@${i.rate}`).join(" | ") },
        ],
        sales
      );
    } else if (tab === "purchases") {
      exportCSV(
        `purchases_${tag}.csv`,
        [
          { key: "date", label: "Date" },
          { key: "type", label: "Type" },
          { key: "supplier", label: "Supplier" },
          { key: "total_kg", label: "Kg", value: (r) => Number(r.total_kg).toFixed(3) },
          { key: "total_amount", label: "Cost (INR)", value: (r) => Number(r.total_amount).toFixed(2) },
          { key: "items", label: "Items", value: (r) => r.items.map((i) => `${i.label} ${i.kg}kg@${i.price_per_kg}`).join(" | ") },
        ],
        purchases
      );
    } else if (tab === "pl" && pl) {
      exportCSV(
        `pl_${tag}.csv`,
        [
          { key: "metric", label: "Metric" },
          { key: "value", label: "Value (INR)" },
        ],
        [
          { metric: "Sales", value: pl.sales_total.toFixed(2) },
          { metric: "Purchase Cost", value: pl.purchase_total.toFixed(2) },
          { metric: "Expenses", value: (pl.expense_total || 0).toFixed(2) },
          { metric: "Profit", value: pl.profit.toFixed(2) },
          { metric: "Sales count", value: pl.sales_count },
          { metric: "Purchase count", value: pl.purchase_count },
        ]
      );
    } else if (tab === "product") {
      exportCSV(
        `product_sales_${tag}.csv`,
        [
          { key: "product_name", label: "Product" },
          { key: "category", label: "Category" },
          { key: "orders", label: "Orders" },
          { key: "kg", label: "Qty", value: (r) => Number(r.kg).toFixed(3) },
          { key: "amount", label: "Revenue (INR)", value: (r) => Number(r.amount).toFixed(2) },
        ],
        productAgg
      );
    } else if (tab === "stock" && stock) {
      exportCSV(
        `stock_forecast_${todayISO()}.csv`,
        [
          { key: "category", label: "Category" },
          { key: "purchased_kg", label: "Purchased (kg)" },
          { key: "sold_kg", label: "Sold (kg)" },
          { key: "remaining_kg", label: "Remaining (kg)" },
          { key: "avg_daily_sold_kg", label: "Avg/day (kg)" },
          { key: "days_until_stockout", label: "Days to stockout" },
        ],
        stock.categories
      );
    }
  };

  const doExportPDF = () => {
    const rows = (cols, data) =>
      `<table><thead><tr>${cols
        .map((c) => `<th${c.right ? ' class="right"' : ""}>${c.label}</th>`)
        .join("")}</tr></thead><tbody>${data
        .map(
          (r) =>
            `<tr>${cols
              .map(
                (c) =>
                  `<td${c.right ? ' class="right"' : ""}>${
                    typeof c.value === "function" ? c.value(r) : r[c.key] ?? ""
                  }</td>`
              )
              .join("")}</tr>`
        )
        .join("")}</tbody></table>`;

    let title = "Report";
    let body = `<h1>EEMAN MEATS</h1><div class="sub">${tab.toUpperCase()} Report · ${from} → ${to}</div>`;

    if (tab === "sales") {
      title = "Sales Report";
      body += rows(
        [
          { key: "date", label: "Date" },
          { key: "channel", label: "Channel" },
          { key: "customer_name", label: "Customer" },
          { label: "Kg", right: true, value: (r) => Number(r.total_kg).toFixed(3) },
          { label: "Amount", right: true, value: (r) => fmtINRPlain(r.total_amount) },
        ],
        sales
      );
    } else if (tab === "purchases") {
      title = "Purchase Report";
      body += rows(
        [
          { key: "date", label: "Date" },
          { key: "type", label: "Type" },
          { key: "supplier", label: "Supplier" },
          { label: "Kg", right: true, value: (r) => Number(r.total_kg).toFixed(3) },
          { label: "Cost", right: true, value: (r) => fmtINRPlain(r.total_amount) },
        ],
        purchases
      );
    } else if (tab === "pl" && pl) {
      title = "Profit & Loss";
      body += `<div class="kpi-grid">
        <div class="kpi"><div class="lbl">Sales</div><div class="val">${fmtINRPlain(pl.sales_total)}</div></div>
        <div class="kpi"><div class="lbl">Purchase Cost</div><div class="val">${fmtINRPlain(pl.purchase_total)}</div></div>
        <div class="kpi"><div class="lbl">Expenses</div><div class="val">${fmtINRPlain(pl.expense_total || 0)}</div></div>
        <div class="kpi"><div class="lbl">Profit</div><div class="val">${fmtINRPlain(pl.profit)}</div></div>
      </div>`;
    } else if (tab === "product") {
      title = "Product-wise Sales";
      body += rows(
        [
          { key: "product_name", label: "Product" },
          { key: "category", label: "Category" },
          { label: "Orders", right: true, key: "orders" },
          { label: "Qty (kg)", right: true, value: (r) => Number(r.kg).toFixed(3) },
          { label: "Revenue", right: true, value: (r) => fmtINRPlain(r.amount) },
        ],
        productAgg
      );
    } else if (tab === "stock" && stock) {
      title = "Stock Forecast";
      body += rows(
        [
          { key: "category", label: "Category" },
          { label: "Purchased", right: true, value: (r) => `${Number(r.purchased_kg).toFixed(2)} kg` },
          { label: "Sold", right: true, value: (r) => `${Number(r.sold_kg).toFixed(2)} kg` },
          { label: "Remaining", right: true, value: (r) => `${Number(r.remaining_kg).toFixed(2)} kg` },
          { label: "Days Left", right: true, value: (r) => r.days_until_stockout ?? "—" },
        ],
        stock.categories
      );
    }
    printPDF(title, body);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto page-enter" data-testid="reports-page">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Insights</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter mt-1 flex items-center gap-3">
          Reports
          <FileBarChart2 className="h-8 w-8 text-zinc-400" />
        </h1>
      </div>

      {/* Date range */}
      <div className="border border-zinc-200 bg-white p-4 mb-5 flex flex-wrap items-center gap-3">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">Range</span>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          data-testid="rpt-from"
          className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
        />
        <span className="text-zinc-400">→</span>
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          data-testid="rpt-to"
          className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
        />
        <div className="flex flex-wrap gap-1">
          {[
            { label: "Today", fn: () => { const t = todayISO(); setFrom(t); setTo(t); } },
            { label: "Yesterday", fn: () => { const d = new Date(); d.setDate(d.getDate() - 1); const y = d.toISOString().slice(0, 10); setFrom(y); setTo(y); } },
            { label: "7 Days", fn: () => { const d = new Date(); d.setDate(d.getDate() - 6); setFrom(d.toISOString().slice(0, 10)); setTo(todayISO()); } },
            { label: "This Month", fn: () => { const t = todayISO(); setFrom(t.slice(0, 8) + "01"); setTo(t); } },
          ].map((q) => (
            <button
              key={q.label}
              onClick={q.fn}
              data-testid={`rpt-quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
              className="border border-zinc-300 px-3 py-2 text-[10px] uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors"
            >
              {q.label}
            </button>
          ))}
        </div>
      </div>

      {/* Sub tabs */}
      <div className="flex border-b border-zinc-200 mb-5">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`rpt-tab-${t.key}`}
            className={`px-5 py-3 text-sm font-medium uppercase tracking-wider border-b-2 ${
              tab === t.key
                ? "border-sky-600 text-zinc-950"
                : "border-transparent text-zinc-500 hover:text-zinc-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Sales */}
      {tab === "sales" && (
        <>
          <div className="flex gap-2 mb-3" data-testid="sales-channel-filter">
            {[
              { k: "all", label: "All" },
              { k: "pos", label: "POS Retail" },
              { k: "wholesale", label: "Wholesale" },
            ].map((o) => (
              <button
                key={o.k}
                onClick={() => setSalesChannel(o.k)}
                data-testid={`sales-filter-${o.k}`}
                className={`px-4 py-2 text-xs uppercase tracking-wider border ${
                  salesChannel === o.k
                    ? "border-sky-600 bg-sky-50 text-sky-700"
                    : "border-zinc-300 text-zinc-700 hover:bg-zinc-50"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
        <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="rpt-sales">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Channel</th>
                <th className="px-4 py-3 text-left">Customer</th>
                <th className="px-4 py-3 text-left">Items</th>
                <th className="px-4 py-3 text-right">Kg</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3 text-right">·</th>
              </tr>
            </thead>
            <tbody>
              {sales.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-sm text-zinc-500">No sales in range</td></tr>
              ) : (
                sales.map((s) => (
                  <tr key={s.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                    <td className="px-4 py-2.5 text-xs">{fmtDate(s.date)}</td>
                    <td className="px-4 py-2.5 text-xs">
                      <span className={`px-2 py-0.5 text-[10px] uppercase tracking-wider ${
                        s.channel === "pos" ? "bg-sky-50 text-sky-700" : "bg-zinc-100 text-zinc-700"
                      }`}>
                        {s.channel}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs">{s.customer_name || "Walk-in"}</td>
                    <td className="px-4 py-2.5 text-xs">{s.items.map(i => i.product_name).join(", ")}</td>
                    <td className="px-4 py-2.5 text-xs font-mono-num text-right">{fmtNum(s.total_kg)}</td>
                    <td className="px-4 py-2.5 text-xs font-mono-num text-right font-semibold">{fmtINR(s.total_amount)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Delete this ${s.channel} sale of ${fmtINR(s.total_amount)}?`)) return;
                          await api.delete(`/sales/${s.id}`);
                          toast.success("Sale deleted");
                          load();
                        }}
                        data-testid={`sale-delete-${s.id}`}
                        className="text-zinc-300 hover:text-rose-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {sales.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                  <td colSpan={4} className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-700 font-bold">
                    Total · {sales.length} sales
                  </td>
                  <td className="px-4 py-3 text-sm font-mono-num text-right font-bold">
                    {fmtNum(sales.reduce((s, x) => s + x.total_kg, 0))} kg
                  </td>
                  <td className="px-4 py-3 text-sm font-mono-num text-right font-bold">
                    {fmtINR(sales.reduce((s, x) => s + x.total_amount, 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        </>
      )}

      {/* Purchases */}
      {tab === "purchases" && (
        <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="rpt-purchases">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Type</th>
                <th className="px-4 py-3 text-left">Items</th>
                <th className="px-4 py-3 text-left">Supplier</th>
                <th className="px-4 py-3 text-right">Kg</th>
                <th className="px-4 py-3 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {purchases.length === 0 ? (
                <tr><td colSpan={6} className="p-8 text-center text-sm text-zinc-500">No purchases in range</td></tr>
              ) : (
                purchases.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                    <td className="px-4 py-2.5 text-xs">{fmtDate(p.date)}</td>
                    <td className="px-4 py-2.5 text-xs">{p.type.replace(/_/g, " ")}</td>
                    <td className="px-4 py-2.5 text-xs">{p.items.map(i => `${i.label} (${i.kg}kg @${i.price_per_kg})`).join(", ")}</td>
                    <td className="px-4 py-2.5 text-xs">{p.supplier || "-"}</td>
                    <td className="px-4 py-2.5 text-xs font-mono-num text-right">{fmtNum(p.total_kg)}</td>
                    <td className="px-4 py-2.5 text-xs font-mono-num text-right font-semibold">{fmtINR(p.total_amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {purchases.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-zinc-900 bg-zinc-50">
                  <td colSpan={4} className="px-4 py-3 text-xs uppercase tracking-wider text-zinc-700 font-bold">
                    Total · {purchases.length} entries
                  </td>
                  <td className="px-4 py-3 text-sm font-mono-num text-right font-bold">
                    {fmtNum(purchases.reduce((s, x) => s + x.total_kg, 0))} kg
                  </td>
                  <td className="px-4 py-3 text-sm font-mono-num text-right font-bold">
                    {fmtINR(purchases.reduce((s, x) => s + x.total_amount, 0))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* P&L */}
      {tab === "pl" && pl && (
        <div data-testid="rpt-pl">
          <div className="mb-3 flex justify-end">
            <button
              onClick={() =>
                downloadFile(
                  `/reports/pl/excel?from_date=${from}&to_date=${to}`,
                  `EEMAN_MEATS_PL_${from}_to_${to}.xlsx`
                ).catch(() => toast.error("Download failed"))
              }
              data-testid="pl-excel-btn"
              className="bg-emerald-600 text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-emerald-700 flex items-center gap-2"
            >
              <FileSpreadsheet className="h-4 w-4" />
              Download Excel
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <div className="border border-zinc-200 bg-white p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Sales</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2">{fmtINR(pl.sales_total)}</div>
            <div className="text-xs text-zinc-500 mt-1">{pl.sales_count} transactions</div>
          </div>
          <div className="border border-zinc-200 bg-white p-6" data-testid="pl-party-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Party Orders</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2">{fmtINR(pl.party_total || 0)}</div>
            <div className="text-xs text-zinc-500 mt-1">{pl.party_count || 0} orders</div>
          </div>
          <div className="border border-zinc-200 bg-white p-6">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Purchase Cost</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2">{fmtINR(pl.purchase_total)}</div>
            <div className="text-xs text-zinc-500 mt-1">{pl.purchase_count} entries</div>
          </div>
          <div className="border border-zinc-200 bg-white p-6" data-testid="pl-expenses-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Expenses</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2">{fmtINR(pl.expense_total || 0)}</div>
            <div className="text-xs text-zinc-500 mt-1">{pl.expense_count || 0} entries</div>
          </div>
          <div className={`border p-6 ${pl.profit >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-sky-50 border-sky-200"}`} data-testid="pl-netprofit-card">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 flex items-center gap-1">
              {pl.profit >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
              {pl.profit >= 0 ? "Net Profit" : "Net Loss"}
            </div>
            <div className={`font-display text-3xl font-bold font-mono-num mt-2 ${pl.profit >= 0 ? "text-emerald-700" : "text-sky-700"}`}>
              {fmtINR(pl.profit)}
            </div>
            <div className="text-xs text-zinc-500 mt-1">
              after purchases & expenses
            </div>
          </div>
          </div>
        </div>
      )}

      {/* Daily Sales Report */}
      {tab === "daily" && (
        <div data-testid="rpt-daily">
          <div className="border border-zinc-200 bg-white p-4 mb-4 flex flex-wrap items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">Report Date</span>
            <input
              type="date"
              value={dailyDate}
              onChange={(e) => setDailyDate(e.target.value)}
              data-testid="daily-date"
              className="border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-600"
            />
            <button
              onClick={() => setDailyDate(todayISO())}
              className="border border-zinc-300 px-3 py-2 text-[10px] uppercase tracking-wider hover:bg-zinc-900 hover:text-white transition-colors"
            >
              Today
            </button>
            {daily && (
              <button
                onClick={() =>
                  printPDF(
                    `Daily Sales Report — ${fmtDate(daily.date)}`,
                    `<h3>Summary</h3>
                     <table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%;">
                       <tr><th align="left">Channel</th><th align="right">Bills</th><th align="right">Amount</th></tr>
                       <tr><td>Retail POS</td><td align="right">${daily.pos.count}</td><td align="right">${fmtINRPlain(daily.pos.amount)}</td></tr>
                       <tr><td>Wholesale</td><td align="right">${daily.wholesale.count}</td><td align="right">${fmtINRPlain(daily.wholesale.amount)}</td></tr>
                       <tr><td>Party Orders</td><td align="right">${daily.party.count}</td><td align="right">${fmtINRPlain(daily.party.amount)}</td></tr>
                       <tr><td><b>TOTAL</b></td><td align="right"><b>${daily.bills.length}</b></td><td align="right"><b>${fmtINRPlain(daily.total_amount)}</b></td></tr>
                       <tr><td>Collected</td><td></td><td align="right">${fmtINRPlain(daily.paid_total)}</td></tr>
                       <tr><td>On Credit</td><td></td><td align="right">${fmtINRPlain(daily.credit_total)}</td></tr>
                     </table>
                     <h3>Product-wise</h3>
                     <table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;width:100%;">
                       <tr><th align="left">Product</th><th align="right">Qty</th><th align="right">Amount</th></tr>
                       ${daily.by_product.map((p) => `<tr><td>${p.product_name}</td><td align="right">${p.unit === "piece" ? Math.round(p.qty) + " pcs" : fmtNum(p.qty, 2) + " kg"}</td><td align="right">${fmtINRPlain(p.amount)}</td></tr>`).join("")}
                     </table>`
                  )
                }
                data-testid="daily-print-btn"
                className="bg-zinc-900 text-white px-4 py-2 text-xs uppercase tracking-wider hover:bg-zinc-800 flex items-center gap-2"
              >
                <Printer className="h-4 w-4" /> Print Report
              </button>
            )}
          </div>

          {daily && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
                {[
                  { label: "Retail POS", v: daily.pos.amount, sub: `${daily.pos.count} bills` },
                  { label: "Wholesale", v: daily.wholesale.amount, sub: `${daily.wholesale.count} bills` },
                  { label: "Party", v: daily.party.amount, sub: `${daily.party.count} orders` },
                  { label: "Total Sales", v: daily.total_amount, sub: `${daily.bills.length} bills`, bold: true },
                  { label: "Collected", v: daily.paid_total, sub: "cash/upi/adv" },
                  { label: "On Credit", v: daily.credit_total, sub: "pending" },
                ].map((c) => (
                  <div key={c.label} className={`border p-4 ${c.bold ? "border-zinc-900 bg-zinc-900 text-white" : "border-zinc-200 bg-white"}`}>
                    <div className={`text-[10px] uppercase tracking-wider ${c.bold ? "text-zinc-300" : "text-zinc-500"}`}>{c.label}</div>
                    <div className="font-display text-xl font-bold font-mono-num mt-1" data-testid={`daily-${c.label.toLowerCase().replace(/ /g, "-")}`}>
                      {fmtINR(c.v)}
                    </div>
                    <div className={`text-[10px] ${c.bold ? "text-zinc-400" : "text-zinc-500"}`}>{c.sub}</div>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-zinc-200 bg-white overflow-x-auto">
                  <div className="px-4 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    Product-wise ({daily.by_product.length})
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {daily.by_product.map((p) => (
                        <tr key={`${p.product_name}-${p.unit}`} className="border-b border-zinc-100">
                          <td className="px-4 py-2">{p.product_name}</td>
                          <td className="px-4 py-2 text-right font-mono-num">
                            {p.unit === "piece" ? `${Math.round(p.qty)} pcs` : `${fmtNum(p.qty, 2)} kg`}
                          </td>
                          <td className="px-4 py-2 text-right font-mono-num font-semibold">{fmtINR(p.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border border-zinc-200 bg-white overflow-x-auto">
                  <div className="px-4 py-3 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                    All Bills ({daily.bills.length})
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {daily.bills.map((b) => (
                        <tr key={b.id} className="border-b border-zinc-100">
                          <td className="px-4 py-2 font-mono-num">#{b.bill_no || "—"}</td>
                          <td className="px-4 py-2 uppercase text-[10px] text-zinc-500">{b.channel}</td>
                          <td className="px-4 py-2 truncate max-w-[120px]">{b.customer_name || "—"}</td>
                          <td className="px-4 py-2 uppercase text-[10px]">{b.payment_mode}</td>
                          <td className="px-4 py-2 text-right font-mono-num font-semibold">{fmtINR(b.total_amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Product-wise */}
      {tab === "product" && (
        <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="rpt-product">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-200 text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-50">
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-right">Orders</th>
                <th className="px-4 py-3 text-right">Kg Sold</th>
                <th className="px-4 py-3 text-right">Amount</th>
              </tr>
            </thead>
            <tbody>
              {productAgg.length === 0 ? (
                <tr><td colSpan={5} className="p-8 text-center text-sm text-zinc-500">No data</td></tr>
              ) : (
                productAgg
                  .sort((a, b) => b.amount - a.amount)
                  .map((p) => (
                    <tr key={p.product_id} className="border-b border-zinc-200 hover:bg-zinc-50">
                      <td className="px-4 py-2.5 text-sm font-medium">{p.product_name}</td>
                      <td className="px-4 py-2.5 text-xs uppercase tracking-wider text-zinc-600">{p.category}</td>
                      <td className="px-4 py-2.5 text-xs font-mono-num text-right">{p.orders}</td>
                      <td className="px-4 py-2.5 text-xs font-mono-num text-right">{fmtNum(p.kg)}</td>
                      <td className="px-4 py-2.5 text-sm font-mono-num text-right font-semibold">{fmtINR(p.amount)}</td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      )}
      {/* Stock Forecast */}
      {tab === "stock" && stock && (
        <div data-testid="rpt-stock">
          <div className="text-xs text-zinc-600 mb-3">
            Based on last <b>{stock.window_days} days</b> of sales velocity · as of {fmtDate(stock.as_of)}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {stock.categories.map((c) => {
              const critical = c.days_until_stockout !== null && c.days_until_stockout < 2;
              const warning =
                c.days_until_stockout !== null && c.days_until_stockout < 5 && !critical;
              return (
                <div
                  key={c.category}
                  className={`border p-5 ${
                    critical
                      ? "border-sky-600 bg-sky-50"
                      : warning
                      ? "border-amber-500 bg-amber-50"
                      : "border-zinc-200 bg-white"
                  }`}
                  data-testid={`stock-${c.category}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-display text-xl font-bold uppercase tracking-wider">
                      {c.category}
                    </div>
                    {c.days_until_stockout !== null && (
                      <div
                        className={`px-2 py-1 text-[10px] uppercase tracking-wider ${
                          critical
                            ? "bg-sky-600 text-white"
                            : warning
                            ? "bg-amber-500 text-white"
                            : "bg-emerald-600 text-white"
                        }`}
                      >
                        {critical
                          ? "Critical"
                          : warning
                          ? "Low"
                          : "Healthy"}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        Purchased
                      </div>
                      <div className="font-mono-num font-semibold">
                        {fmtNum(c.purchased_kg)} kg
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        Sold
                      </div>
                      <div className="font-mono-num font-semibold">{fmtNum(c.sold_kg)} kg</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        Remaining
                      </div>
                      <div className="font-mono-num font-bold text-lg">
                        {fmtNum(c.remaining_kg)} kg
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        Avg / Day
                      </div>
                      <div className="font-mono-num font-semibold">
                        {fmtNum(c.avg_daily_sold_kg)} kg
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-zinc-200 pt-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                      Days Until Stockout
                    </div>
                    <div className="font-display text-3xl font-bold font-mono-num mt-1">
                      {c.days_until_stockout === null
                        ? "—"
                        : `${c.days_until_stockout} days`}
                    </div>
                    {c.days_until_stockout === null && (
                      <div className="text-xs text-zinc-500 mt-1">
                        No sales velocity data yet
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="text-[10px] text-zinc-500 mt-4 max-w-2xl">
            Note: Stock forecast assumes raw material (live chicken / mutton incl. offals) drives all
            sales kg. Wastage during processing is naturally reflected in lower remaining stock vs
            sold kg.
          </div>
        </div>
      )}
    </div>
  );
}
